// A05 — Admin Forgot Password (sub-plan 13 §13.4 UNAUTH route).
// POSTs `/v1/auth/forgot-password` with captcha. Uses normalizePhoneHK if the
// input looks like a phone (digits-only or +852).
//
// Server response is always-200 to avoid account enumeration; we always show
// "If an account exists, a reset link has been sent."

import { useRef, useState, type FormEvent } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link as MuiLink,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { tokens } from "@findgeeks/ui-tokens";
import { normalizePhoneHK, validatePhoneHK } from "@findgeeks/shared";
import { ENV } from "@/lib/env";
import {
  CaptchaWidget,
  type CaptchaWidgetHandle,
} from "@/components/CaptchaWidget";

// Treat as phone only when the input starts with '+' or is digits-only —
// otherwise it's ambiguous between phone, email-without-@, and username.
// (Admin usernames can contain digits, e.g. "admin01".)
function looksLikePhone(input: string): boolean {
  const t = input.trim();
  if (!t) return false;
  return t.startsWith("+") || /^\d+$/.test(t);
}

export function ForgotPassword() {
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const captchaRef = useRef<CaptchaWidgetHandle | null>(null);

  const canSubmit =
    identifier.trim().length > 0 && captchaToken !== null && !submitting;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    // Server-side ForgotPasswordReq now accepts username, email, or
    // phone. Normalise only if the input looks like an HK phone; pass
    // everything else through as-is (the backend resolves usernames
    // against user_profiles.username before dispatching).
    let identifierOut: string;
    const raw = identifier.trim();
    if (looksLikePhone(raw)) {
      if (!validatePhoneHK(raw)) {
        setError(
          "That phone number doesn't look right. Use 8 digits or +852 followed by 8 digits, or enter your username / email instead.",
        );
        setSubmitting(false);
        return;
      }
      identifierOut = normalizePhoneHK(raw);
    } else {
      identifierOut = raw;
    }
    const payload: Record<string, string> = { identifier: identifierOut };

    try {
      const res = await fetch(`${ENV.API_BASE_URL}/v1/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Captcha-Token": captchaToken!,
        },
        body: JSON.stringify(payload),
      });
      // Discard captcha on every response.
      captchaRef.current?.reset();
      setCaptchaToken(null);

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.message ?? body?.error ?? "Request failed");
        return;
      }
      // Navigate to the code-entry page. We forward the identifier the
      // user typed so they don't have to retype it. We do NOT forward
      // the resolved email even when the server resolved a username —
      // that would leak the underlying email to anyone who knows the
      // username.
      navigate("/reset-with-code", {
        replace: true,
        state: { identifier: identifierOut },
      });
    } catch (err) {
      setError((err as Error).message ?? "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // Same background image as SignIn (apps/admin/public/login-bg.png).
        // signInBg color stays as a fallback if the image fails to load.
        backgroundColor: tokens.colors.admin.signInBg,
        backgroundImage: "url(/login-bg.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Paper elevation={6} sx={{ width: "100%", maxWidth: 450, p: 4 }}>
        <Typography
          component="h1"
          sx={{
            fontSize: tokens.font.sizes.h1,
            fontWeight: tokens.font.weights.semibold,
            mb: 1,
            color: tokens.colors.admin.textHeading,
          }}
        >
          Forgot Password
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 3, color: tokens.colors.admin.textBody }}
        >
          Enter your username, email, or HK phone number. We'll send you a
          6-digit code by email.
        </Typography>

        <Box component="form" onSubmit={onSubmit} noValidate>
          <TextField
            label="Username, email, or phone"
            fullWidth
            required
            autoComplete="username"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 2 }}>
            <CaptchaWidget
              ref={captchaRef}
              onToken={(t) => setCaptchaToken(t)}
              onError={() =>
                setError("Unable to verify captcha. Please try again.")
              }
            />
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!canSubmit}
            sx={{
              py: 1.25,
              borderRadius: 9999,
              background: tokens.colors.admin.primary,
              "&:hover": { background: tokens.colors.admin.primary },
            }}
          >
            {submitting ? <CircularProgress size={20} /> : "Send reset code"}
          </Button>
        </Box>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <MuiLink
            component={RouterLink}
            to="/login"
            underline="hover"
            variant="body2"
          >
            Back to Sign In
          </MuiLink>
        </Box>
      </Paper>
    </Box>
  );
}
